import { useEffect } from "react";
import { Play } from "lucide-react";
import { preloadSounds } from "@/lib/soundPlayer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { Label } from "@/components/Base/Label";
import { Switch } from "@/components/Base/Switch";
import { toast } from "@/hooks/useToast";
import { useSoundPreferences } from "@/hooks/useSoundPreferences";
import { playToastSoundEffect } from "@/hooks/useToastSound";
import {
  TOAST_SOUND_EFFECTS,
  ToastSoundCategory,
  ToastSoundEffectId,
} from "@/lib/toastSound";
import { cn } from "@/lib/utils";

const CATEGORY_LABELS: { category: ToastSoundCategory; label: string }[] = [
  { category: "automation", label: "Automation notifications" },
  { category: "promiseTime", label: "Promise time" },
  { category: "customerUpdate", label: "Customer updates" },
  { category: "task", label: "Tasks" },
];

interface SoundPreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Per-user toast sound settings: which effect plays, and an independent
 * on/off switch for each kind of toast. Every change saves immediately; the
 * controls show the stored value plus a pending overlay (see
 * useSoundPreferences), so a failed save drops the overlay and the control
 * falls back to the stored value.
 * @param {SoundPreferencesDialogProps} props - Dialog open state.
 * @returns {JSX.Element} The dialog.
 */
export const SoundPreferencesDialog = ({
  open,
  onOpenChange,
}: SoundPreferencesDialogProps) => {
  const { preferences, save, canSave } = useSoundPreferences();

  // Warm every effect while the dialog is open so a first preview doesn't
  // wait on the load.
  useEffect(() => {
    if (!open) return;
    Promise.resolve(
      preloadSounds(TOAST_SOUND_EFFECTS.map((effect) => effect.id))
    ).catch((error: unknown) => console.debug("Sound preload failed:", error));
  }, [open]);

  const persist = (next: typeof preferences) => {
    if (!canSave) return;
    save(next).catch((error: unknown) =>
      toast({
        title: "Couldn't save sound settings",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      })
    );
  };

  const selectEffect = (effect: ToastSoundEffectId) =>
    persist({ ...preferences, effect });

  const setCategory = (category: ToastSoundCategory, value: boolean) =>
    persist({
      ...preferences,
      enabled: { ...preferences.enabled, [category]: value },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sound alerts</DialogTitle>
          <DialogDescription>
            Choose the sound toast notifications play, and which kinds of toast
            make one. These settings follow your login on every device.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">Sound</p>
          <div
            role="radiogroup"
            aria-label="Sound"
            className="flex flex-col gap-1"
          >
            {TOAST_SOUND_EFFECTS.map(({ id, label }) => {
              const selected = preferences.effect === id;
              return (
                <div key={id} className="flex items-center gap-2">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => selectEffect(id)}
                    className={cn(
                      "flex-1 rounded-md border px-3 py-2 text-left text-sm",
                      selected
                        ? "border-accent bg-accent/10 font-medium"
                        : "border-border"
                    )}
                  >
                    {label}
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={`Preview ${label}`}
                    onClick={() => playToastSoundEffect(id)}
                  >
                    <Play className="size-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">Play a sound for</p>
          {CATEGORY_LABELS.map(({ category, label }) => (
            <div key={category} className="flex items-center justify-between">
              <Label htmlFor={`sound-${category}`}>{label}</Label>
              <Switch
                id={`sound-${category}`}
                checked={preferences.enabled[category]}
                onCheckedChange={(value) => setCategory(category, value)}
              />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
